import { Callable, Optional, Maybe } from '@vimeo/core/types/generic';

import { ChatManager, IChatBufferValue } from './ChatManager';
import { interactionToolsConfig } from '../../../../configs/InteractionToolsConfig';
import { TFirebaseReference, TFirebaseSnapshot } from '../../../../types/firebase';
import { CallablePlaceholder } from '../../../../types/general';
import {
  EChatType,
  IChatMessageRecord,
  TChatBanList,
  TChatHistory,
  TChatParticipants,
} from '../../../../types/interaction_tools';
import { getRefValue, offRefEvent, onRefEvent } from '../../../../utils/firebase/cruds';
import { collectMessageInteractionErrors } from '../../../../utils/firebase/interaction_objects.validators';
import {
  reBufferizeChatMessages,
  transformBulkedChatMessages,
  transformChatMessage,
} from '../../../../utils/firebase/transform';
import { TErrorMessages } from '../../../../utils/firebase/validator_utils';
import { omit } from '../../../../utils/general/object';
import { getAbsoluteNow } from '../../../../utils/general/time';
import { InMemoryBuffer } from '../../../../utils/store/memory_buffer';
import {
  checkErrorsAndTrack,
  ELiveErrorCategory,
  trackLiveError,
} from '../../../../utils/tracking';

/**
 * Check current in-memory stored messages and once buffer reaches limits -> cut it down and force data processing.
 */
function checkChatBuffer(manager: ChatManager, buffer: InMemoryBuffer<IChatBufferValue>): void {
  const { messages: bufferedMessages, participants: bufferedParticipants } = buffer.getValue();
  const messagesCount: number = Object.keys(bufferedMessages).length;

  if (
    messagesCount >
    interactionToolsConfig.CHAT.IN_MEMORY_MESSAGES_BUFFER +
      interactionToolsConfig.CHAT.IN_MEMORY_MESSAGES_LIMIT
  ) {
    manager.log.info('Cleaning up chat messages buffer:', buffer.name);

    const [filteredMessages, filteredUsers] = reBufferizeChatMessages(
      bufferedMessages,
      bufferedParticipants,
      interactionToolsConfig.CHAT.IN_MEMORY_MESSAGES_LIMIT,
    );

    buffer.setValue({
      messages: filteredMessages,
      participants: filteredUsers,
    });
  }
}

/**
 * Hydrate list of blocked users to know which messages should be ignored.
 */
export async function hydrateBanList(manager: ChatManager, chatType: EChatType): Promise<void> {
  manager.log.info('Hydrating ban list');

  const banListRef: Optional<TFirebaseReference> = manager.getChatBanListRef(chatType);

  if (banListRef) {
    try {
      const value: TChatBanList = await getRefValue(manager.firebase, banListRef);

      if (value) {
        const banList: TChatBanList = manager.context[chatType].banList;

        manager.setContext({
          [chatType]: manager.context[chatType].asMerged({
            banList: { ...value, ...banList },
          }),
        });

        manager.log.info('Loaded ban list:', value);
      } else {
        manager.log.info('No users banned in current event:', value);
      }
    } catch (error) {
      manager.log.error('Ban list hydration failed:', error);

      trackLiveError(error, {
        method: 'hydrateBanList',
        category: ELiveErrorCategory.INTERACTION,
      });
    }
  }
}

/**
 * Subscription to public chat ban list.
 */
export function subscribeToBanList(manager: ChatManager, chatType: EChatType): Callable {
  const onBlockUser = (snapshot: TFirebaseSnapshot) => {
    const value = snapshot.val();

    if (!snapshot.key) {
      return manager.log.error('Failed to ban user, no snapshot key:', value);
    }

    if (value) {
      manager.log.info('Received block user from remote:', value);

      const banList: TChatBanList = manager.context[chatType].banList;

      manager.setContext({
        [chatType]: manager.context[chatType].asMerged({
          banList: { ...banList, [snapshot.key]: value },
        }),
      });
    }
  };

  const onUnblockUser = (snapshot: TFirebaseSnapshot) => {
    if (!snapshot.key) {
      return manager.log.error('Failed to unban user, no snapshot key:', snapshot.key);
    } else {
      manager.log.info('Received unban user:', snapshot.key);

      const banList: TChatBanList = manager.context[chatType].banList;

      manager.setContext({
        [chatType]: manager.context[chatType].asMerged({
          banList: omit(banList, snapshot.key),
        }),
      });
    }
  };

  const banListRef: Optional<TFirebaseReference> = manager.getChatBanListRef(chatType);

  if (banListRef) {
    onRefEvent(manager.firebase, banListRef, 'child_added', onBlockUser, 'ban-list');
    onRefEvent(manager.firebase, banListRef, 'child_removed', onUnblockUser, 'ban-list');

    return () => {
      offRefEvent(manager.firebase, banListRef, 'child_added', onBlockUser);
      offRefEvent(manager.firebase, banListRef, 'child_removed', onUnblockUser);
    };
  } else {
    return CallablePlaceholder;
  }
}

/**
 * Hydrate chat history with a single request.
 * Prevents 100s re-renders when page is initializing.
 */
export async function hydrateChatHistory(
  manager: ChatManager,
  chatRef: TFirebaseReference,
  buffer: InMemoryBuffer<IChatBufferValue>,
): Promise<void> {
  manager.log.info('Hydrating chat messages history');

  const chatRefName: Maybe<string> = chatRef.parent?.parent?.key?.toString();

  try {
    const chatMessageRecords: Record<string, IChatMessageRecord> = await getRefValue(
      manager.firebase,
      manager.firebase.database.query(
        chatRef,
        manager.firebase.database.limitToLast(interactionToolsConfig.CHAT.IN_MEMORY_MESSAGES_LIMIT),
      ),
    );

    // filtering out possible corrupted chat messages and log them to the new relic
    Object.entries(chatMessageRecords || {}).forEach(([id, message]) => {
      const possibleErrors = collectMessageInteractionErrors(message);
      const isValid = checkErrorsAndTrack({
        possibleErrors,
        data: message,
        methodName: 'hydrateChatHistory',
      });
      if (!isValid) {
        delete chatMessageRecords[id];
      }
    });

    if (chatMessageRecords) {
      const { messages, users } = transformBulkedChatMessages(chatMessageRecords);

      const { messages: bufferedMessages, participants: bufferedParticipants } = buffer.getValue();

      const newMessages: TChatHistory = { ...bufferedMessages, ...messages };
      const newParticipants: TChatParticipants = { ...users, ...bufferedParticipants };

      buffer.setValue({
        messages: newMessages,
        participants: newParticipants,
      });

      manager.log.info('Hydrated messages for chat ref:', chatRefName);
    } else {
      manager.log.info('Chat history was empty for chat ref:', chatRefName);
    }
  } catch (error) {
    manager.log.error('Chat history hydration failed for chat ref:', error, chatRefName);

    trackLiveError(error, {
      method: 'hydrateChatHistory',
      category: ELiveErrorCategory.INTERACTION,
      data: {
        chatType: chatRefName,
      },
    });
  }
}

/**
 * Subscription to chat messages with buffer updates.
 */
export function subscribeToChatMessages(
  manager: ChatManager,
  chatRef: TFirebaseReference,
  buffer: InMemoryBuffer<IChatBufferValue>,
): Callable {
  const onChatMessagePush = (snapshot: TFirebaseSnapshot) => {
    // check if chat is hydrated for avoiding conflict in updates and extra render
    if (!manager.context.isHydrated) {
      return manager.log.info('Chat history not hydrated yet');
    }

    const value = snapshot.val();

    if (!snapshot.key) {
      return manager.log.error('Failed to add message, no snapshot key:', value);
    }

    try {
      // filtering out possible corrupted chat messages and log them to the new relic
      const possibleErrors: TErrorMessages = collectMessageInteractionErrors(value);
      const isValid: boolean = checkErrorsAndTrack({
        possibleErrors,
        data: value,
        methodName: 'onChatMessagePush',
      });

      if (value && isValid) {
        const [message, user] = transformChatMessage(snapshot.key, value);
        const { messages: bufferedMessages, participants: bufferedParticipants } =
          buffer.getValue();

        buffer.setValue({
          messages: { ...bufferedMessages, [snapshot.key]: message },
          participants: { ...bufferedParticipants, [user.id]: user },
        });

        checkChatBuffer(manager, buffer);
      }
    } catch (error) {
      trackLiveError(error, {
        category: ELiveErrorCategory.INTERACTION,
        method: 'onChatMessagePush',
        data: { key: snapshot.key, roomId: manager.context.config.roomId },
      });
    }
  };

  const onChatMessageDeleted = (snapshot: TFirebaseSnapshot) => {
    if (!snapshot.key) {
      return manager.log.error('Failed to remove message, no snapshot key:', snapshot.key);
    } else {
      const { messages: bufferedMessages, participants: bufferedParticipants } = buffer.getValue();

      /**
       * Proceed message removal only if it is in current in-memory storage.
       */
      if (bufferedMessages[snapshot.key]) {
        buffer.setValue({
          messages: omit(bufferedMessages, snapshot.key),
          participants: bufferedParticipants,
        });
      }
    }
  };

  onRefEvent(
    manager.firebase,
    manager.firebase.database.query(chatRef, manager.firebase.database.limitToLast(25)),
    'child_added',
    onChatMessagePush,
    'chat-messages',
  );
  onRefEvent(manager.firebase, chatRef, 'child_removed', onChatMessageDeleted, 'chat-messages');

  return () => {
    offRefEvent(manager.firebase, chatRef, 'child_added', onChatMessagePush);
    offRefEvent(manager.firebase, chatRef, 'child_removed', onChatMessageDeleted);
  };
}

/**
 * Subscription to chat status disabled/enabled.
 */
export function subscribeToChatStatus(
  manager: ChatManager,
  metaChatEnabledRef: TFirebaseReference,
): Callable {
  const onChatStatusChange = (snapshot: TFirebaseSnapshot) => {
    const isChatEnabled: boolean = Boolean(snapshot.val());

    if (isChatEnabled !== manager.context.isEnabled) {
      manager.log.info('Chat status change:', isChatEnabled);
      manager.setContext({ isEnabled: Boolean(isChatEnabled), enabledAt: getAbsoluteNow() });
    }
  };

  onRefEvent(manager.firebase, metaChatEnabledRef, 'value', onChatStatusChange, 'chat-status');
  return () => offRefEvent(manager.firebase, metaChatEnabledRef, 'value', onChatStatusChange);
}
